import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, AlertCircle, CreditCard as CardIcon, Sparkles, Filter } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { AdminAPI } from '@/lib/admin-api';
import { formatINR } from '@/lib/format';

const STATUS_BADGE = {
  active: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  draft: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  inactive: 'bg-slate-500/10 text-slate-600 border-slate-500/30',
};

export default function AdminCards() {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [bankId, setBankId] = useState('all');
  const [statusF, setStatusF] = useState('all');
  const [typeF, setTypeF] = useState('all');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, b] = await Promise.all([
        AdminAPI.cards.list({
          q: q || undefined,
          bank_id: bankId !== 'all' ? bankId : undefined,
          status: statusF !== 'all' ? statusF : undefined,
          type: typeF !== 'all' ? typeF : undefined,
        }),
        banks.length ? Promise.resolve(banks) : AdminAPI.banks.list({ includeInactive: true }),
      ]);
      setCards(c || []);
      if (b && !banks.length) setBanks(b);
    } catch (e) {
      setError(e.message || 'Failed to load cards');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bankId, statusF, typeF]);
  useEffect(() => {
    const t = setTimeout(() => load(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const totals = useMemo(() => ({
    total: cards.length,
    active: cards.filter((c) => c.status === 'active').length,
    draft: cards.filter((c) => c.status === 'draft').length,
  }), [cards]);

  return (
    <div className="space-y-4" data-testid="admin-cards-page">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-medium">Catalog</div>
          <h1 className="mt-1.5 font-display text-xl sm:text-2xl font-semibold tracking-tight">Cards</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            {totals.total} cards · {totals.active} active · {totals.draft} draft
          </p>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => navigate('/admin/cards/new')} data-testid="admin-cards-create-button">
          <Plus className="h-4 w-4" /> New card
        </Button>
      </div>

      <Card className="p-3 flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by card name, bank, or tier..." className="pl-9 h-9 border-transparent bg-[hsl(var(--surface-2))]" data-testid="admin-cards-search-input" />
        </div>
        <div className="grid grid-cols-3 lg:flex gap-2 lg:flex-wrap">
          <Select value={bankId} onValueChange={setBankId}>
            <SelectTrigger className="h-9 w-full lg:w-[180px]" data-testid="admin-cards-filter-bank"><Filter className="h-3.5 w-3.5" /><SelectValue placeholder="All banks" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All banks</SelectItem>
              {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusF} onValueChange={setStatusF}>
            <SelectTrigger className="h-9 w-full lg:w-[140px]" data-testid="admin-cards-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeF} onValueChange={setTypeF}>
            <SelectTrigger className="h-9 w-full lg:w-[140px]" data-testid="admin-cards-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="rewards">Rewards</SelectItem>
              <SelectItem value="cashback">Cashback</SelectItem>
              <SelectItem value="miles">Miles</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {error && (
        <Card className="p-3 border-rose-500/30 bg-rose-500/5 flex items-start gap-2 text-sm text-rose-700 dark:text-rose-300" data-testid="admin-error-alert">
          <AlertCircle className="h-4 w-4 mt-0.5" /><span>{error}</span>
          <Button size="sm" variant="ghost" className="ml-auto" onClick={load}>Retry</Button>
        </Card>
      )}

      <Card className="overflow-x-auto">
        <Table data-testid="admin-cards-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[35%]">Card</TableHead>
              <TableHead className="hidden md:table-cell">Tier</TableHead>
              <TableHead className="hidden lg:table-cell">Network</TableHead>
              <TableHead className="text-right">Annual fee</TableHead>
              <TableHead className="hidden lg:table-cell text-right">Pt value</TableHead>
              <TableHead className="hidden sm:table-cell">Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
              ))
            ) : cards.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-12 text-center text-muted-foreground" data-testid="admin-cards-empty-state">
                  <CardIcon className="h-6 w-6 mx-auto opacity-50 mb-2" />
                  <div className="text-sm">No cards match the current filters.</div>
                </TableCell>
              </TableRow>
            ) : cards.map((c) => (
              <TableRow key={c.id} className="h-11 cursor-pointer" data-testid={`admin-cards-row-${c.id}`} onClick={() => navigate(`/admin/cards/${c.id}`)}>
                <TableCell className="py-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{c.bank} — {c.name}</div>
                    <div className="text-[10px] text-muted-foreground font-mono-num">{c.id}</div>
                    {/* Mobile-only condensed status row beneath the name (since Tier/Network/Status are hidden on smaller breakpoints) */}
                    <div className="sm:hidden mt-1 flex items-center gap-1.5 flex-wrap">
                      <Badge variant="outline" className="text-[9px]">{c.tier}</Badge>
                      <Badge variant="outline" className={`text-[9px] ${STATUS_BADGE[c.status] || ''}`}>{c.status || 'active'}</Badge>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell py-2"><Badge variant="outline" className="text-[10px]">{c.tier}</Badge></TableCell>
                <TableCell className="hidden lg:table-cell py-2 text-xs uppercase tracking-wide font-medium">{c.network}</TableCell>
                <TableCell className="py-2 text-right font-mono-num text-xs">{formatINR(c.annual_fee || 0)}</TableCell>
                <TableCell className="hidden lg:table-cell py-2 text-right font-mono-num text-xs">₹{(c.point_value_inr || 0).toFixed(2)}</TableCell>
                <TableCell className="hidden sm:table-cell py-2">
                  <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[c.status] || ''}`}>{c.status || 'active'}</Badge>
                </TableCell>
                <TableCell className="py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-flex items-center gap-1">
                    <Button asChild size="sm" variant="ghost" className="h-7 text-xs hidden sm:inline-flex" data-testid={`admin-cards-edit-${c.id}`}>
                      <Link to={`/admin/cards/${c.id}`}>Edit</Link>
                    </Button>
                    <Button asChild size="sm" variant="ghost" className="h-7 text-xs gap-1 text-primary hover:text-primary" data-testid={`admin-cards-rules-${c.id}`}>
                      <Link to={`/admin/cards/${c.id}/rules`}><Sparkles className="h-3 w-3" /> Rules</Link>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
